/* eslint-disable array-callback-return */
import React, { useRef, useEffect, useState, type FC, type JSX } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import ReactDOM from 'react-dom';

import { Icon } from '@/app/common/icons/Icon';
import { MaskIcon } from '@/app/common/icons/MaskIcon';
import type { ControlProps, Option, Target } from '../../index';
import InputField from '../InputField';
import List from './List';
import { MobileTitle, NoResults, Overlay, SearchListContainer } from './styled';
import { getOptions } from './utils';

interface SearchProps extends ControlProps {
  options: Option[];
  name: string;
  limit: number;
  label: string;
  noResultsText: string;
  containerTitle: string;
  onUpdate: (e: Target) => Promise<void>;
}

const SearchList: FC<SearchProps> = (props): JSX.Element => {
  const { onUpdate, limit, columns, name, label, noResultsText, isMobile, containerTitle, disabled, containerRef } =
    props;
  const innerRef = useRef<HTMLInputElement>(null);
  const hasUnselectOther = props.options.filter((x) => x.unselectOther).length > 0;
  const [options, setOptions] = useState(getOptions(props.options, props.value, props.value));
  const [overlayOptions, setOverlayOptions] = useState(props.options);
  const [value, setValue] = useState(props.value);
  const [selections, setSelections] = useState<number[]>(props.value);
  const [isOpen, setIsOpen] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const searchListContainerRef = useRef<HTMLDivElement>(null);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newOptions = props.options.filter((x) => {
      let hasWord = x.label ? new RegExp(`^${e.target.value}`, 'i').test(x.label.toString()) : false;
      if (x?.label) {
        x.label
          .toString()
          .split(' ')
          .map((y) => {
            if (new RegExp(`^${e.target.value}`, 'i').test(y)) {
              hasWord = true;
            }
          });
      }
      return hasWord;
    });
    newOptions = newOptions.filter((x) => value.indexOf(x.value) === -1);
    setOverlayOptions(newOptions);
    setInputValue(e.target.value);

    if (e.target.value.toLowerCase().length > 0) {
      setIsOpen(true);
      if (newOptions.length === 0) {
        setNoResults(true);
      } else {
        setNoResults(false);
      }
    } else {
      setIsOpen(false);
      setNoResults(false);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      setIsOpen(false);
      setCurrentIndex(0);
      handleClose();
      setInputValue('');
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) {
        if (currentIndex === 0) {
          handleItemClick(overlayOptions[0]);
        } else {
          handleItemClick(overlayOptions[currentIndex]);
        }
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentIndex === overlayOptions.length - 1) {
        const currentItem = overlayOptions[0];
        scrollOnFocus(currentItem);
        setCurrentIndex(0);
      } else {
        const currentItem = overlayOptions[currentIndex + 1];
        scrollOnFocus(currentItem);
        setCurrentIndex(currentIndex + 1);
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentIndex === 0) {
        const currentItem = overlayOptions[overlayOptions.length - 1];
        scrollOnFocus(currentItem);
        setCurrentIndex(overlayOptions.length - 1);
      } else {
        const currentItem = overlayOptions[currentIndex - 1];
        scrollOnFocus(currentItem);
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  const handleItemClick = (item: Option) => {
    if (disabled) return;
    if (!item) return;
    if (!selections || (selections && selections.filter((x) => x === item.value).length === 0)) {
      const newVal = [...(selections || []), item.value as number];
      setSelections(newVal);
    }
    let newValue = [];
    if (hasUnselectOther) {
      if (item.unselectOther) {
        if (value && value.indexOf(item.value) !== -1) {
          newValue = [];
        } else {
          newValue = [item.value];
        }
      } else {
        if (value && value.indexOf(item.value) !== -1) {
          newValue = value.filter((o: number) => o !== item.value);
        }
        if ((value && value.indexOf(item.value) === -1) || !value) {
          const unselectOtherValue = props.options.filter((x) => x.unselectOther)[0].value;
          const newV = value ? [...value, item.value] : [item.value];
          newValue = newV.filter((x) => x !== unselectOtherValue);
        }
      }
    } else {
      if (value && value.indexOf(item.value) !== -1) {
        newValue = value.filter((o: number) => o !== item.value);
      }
      if ((value && value.indexOf(item.value) === -1) || !value) {
        newValue = value ? [...value, item.value] : [item.value];
      }
    }

    setValue(newValue);
    setIsOpen(false);
    setCurrentIndex(0);
    handleClose();
    setInputValue('');
  };

  useEffect(() => {
    setOptions(getOptions(props.options, props.value, selections));

    if (value !== props.value) {
      onUpdate({ target: { name, value } });

      if (searchListContainerRef.current) {
        const container = searchListContainerRef.current.parentNode as HTMLDivElement;
        if (container) {
          if (value && value.length > 0) {
            const optionsCount = value.length + (selections?.filter((x) => value.indexOf(x) === -1).length || 0);
            const height = columns === 2 ? 68 + Math.ceil(optionsCount / 2) * 36 + 16 : 68 + optionsCount * 36 + 16;

            container.setAttribute('style', `height: ${height}px;`);
          } else if (!selections || value === undefined) {
            setSelections([]);
            const height = 68 + 16;

            container.setAttribute('style', `height: ${height}px;`);
          }
        }
      }
    }
  }, [value]);

  useEffect(() => {
    if (value !== props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  useOnclickOutside(
    () => {
      if (isOpen && !isMobile) {
        setIsOpen(false);
        setNoResults(false);
        setCurrentIndex(0);
        setInputValue('');
        if (searchListContainerRef.current) {
          const container = searchListContainerRef.current.parentNode?.parentNode as HTMLDivElement;
          if (container.classList.contains('overflow-visible')) {
            container.classList.remove('overflow-visible');
          }
        }
      }
    },
    {
      refs: [overlayRef],
      ignoreClass: [
        `oss-ui-form-overlay-${name}`,
        `oss-ui-form-input-${name}`,
        `oss-ui-form-icon-${name}`,
        `oss-ui-form-label-${name}`,
      ],
    }
  );

  const scrollOnFocus = (item: Option) => {
    const listbox = overlayRef.current;

    if (listbox) {
      const element = (listbox as HTMLDivElement).querySelector(`#exp_elem_${name}_${item.value}`);
      if (element) {
        if (listbox.scrollHeight > listbox.clientHeight) {
          const scrollBottom = listbox.clientHeight + listbox.scrollTop;
          const elementBottom = (element as HTMLLIElement).offsetTop + (element as HTMLLIElement).offsetHeight;
          if (elementBottom > scrollBottom) {
            listbox.scrollTop = elementBottom - listbox.clientHeight;
          } else if ((element as HTMLLIElement).offsetTop < listbox.scrollTop) {
            listbox.scrollTop = (element as HTMLLIElement).offsetTop;
          }
        }
      }
    }
  };

  const handleInputFocus = (val: boolean) => {
    if (val) {
      setIsOpen(val);
      if (searchListContainerRef.current) {
        const containerOverflow = searchListContainerRef.current.parentNode?.parentNode as HTMLDivElement;
        const containerFullScreen = searchListContainerRef.current.parentNode as HTMLDivElement;
        if (!containerOverflow.classList.contains('overflow-visible')) {
          containerOverflow.classList.add('overflow-visible');
        }
        if (isMobile) {
          if (!containerFullScreen.classList.contains('full-screen')) {
            containerFullScreen.classList.add('full-screen');
          }
        }
      }
    }
  };

  useEffect(() => {
    const newSelections = selections
      .map((x) => props.options.filter((y) => y.value === x)[0].value as number)
      .filter((z) => !!z);
    setOptions(getOptions(props.options, value, newSelections));
    setOverlayOptions(props.options);
    setSelections(newSelections);
    if (searchListContainerRef.current && newSelections.length === 0 && (!value || value.length === 0)) {
      const container = searchListContainerRef.current.parentNode as HTMLDivElement;
      const height = 68 + 16;
      container.setAttribute('style', `height: ${height}px;`);
    }
  }, [props.options]);

  const handleClose = () => {
    if (isMobile && searchListContainerRef.current) {
      const container = searchListContainerRef.current.parentNode as HTMLDivElement;
      container.classList.remove('full-screen');
      setIsOpen(false);
    }
  };

  const iconLeft = (
    <Icon
      category="UI Essentials"
      name="icon-magnifying-glass"
      size={19}
      color="iconSecondary"
      className={`oss-ui-form-icon-${name}`}
    />
  );

  return (
    <>
      {isOpen && isMobile && (
        <MobileTitle>
          <MaskIcon category="Semantics" name="icon-cross" size={24} onClick={handleClose} />
          <span>{containerTitle}</span>
        </MobileTitle>
      )}
      <SearchListContainer ref={searchListContainerRef} isOpen={isOpen} disabled={disabled} isMobile={isMobile}>
        <InputField
          name={name}
          value={inputValue}
          label={label}
          iconLeft={iconLeft}
          ref={innerRef}
          className={`oss-ui-form-input-${name}`}
          isValid={!noResults}
          onChange={(e) => handleOnChange(e)}
          onKeyDown={(e) => handleInputKeyDown(e)}
          onFocus={handleInputFocus}
        />
        {isOpen && isMobile ? null : (
          <List
            options={options}
            columns={columns}
            limit={value && value.length > limit ? value.length : limit}
            value={value}
            name={name}
            handleItemClick={handleItemClick}
          />
        )}
        {isOpen &&
          searchListContainerRef.current &&
          ReactDOM.createPortal(
            <Overlay
              className={`oss-ui-form-overlay-${name}`}
              ref={overlayRef}
              hasContainer={!!containerRef}
              isMobile={isMobile}
            >
              {noResults && (
                <NoResults role="status" aria-live="polite">
                  {noResultsText}
                </NoResults>
              )}
              <List
                options={overlayOptions
                  .filter((x) => (value || []).indexOf(x.value) === -1)
                  .filter((x) => (selections || []).indexOf(x.value as number) === -1)}
                value={value}
                name={name}
                isOverlay={true}
                currentIndex={currentIndex}
                handleItemClick={handleItemClick}
              />
            </Overlay>,
            searchListContainerRef.current
          )}
      </SearchListContainer>
    </>
  );
};

export default SearchList;
